// utils/learning/streamResume.js
import { getBaseURL, parseChunkBuffer, CONTENT_TYPE_JSON } from '@/utils/localStream';
import { getToken, getUser } from '@/utils/auth';

/**
 * 续传：请求后端缓存，从 offset（前端已收完整 JSON 分片数）之后接续推送。
 * 后端行为：回放 entry.tokens[offset:] → 轮询 tokens 追加新分片 → 生成终态后结束。
 * @param {string} requestId  发起时的 aiContent.id（后端缓存 key）
 * @param {number} offset     前端已收到的完整 JSON 分片数（t.receivedCount）
 * @param {Function} onJson   (json对象) => void，每解析出一个完整 JSON 分片回调
 * @param {AbortController} controller
 * @throws Error(.status === 404) 缓存不存在/已过期/已被 cancel → 前端降级提示
 */
export async function resumeStream(requestId, offset, onJson, controller) {
  const resp = await fetch(`${getBaseURL()}/resume-stream`, {
    method: 'POST',
    headers: new Headers({
      "Content-Type": CONTENT_TYPE_JSON,
      "Authorization": getToken(),
      "username": getUser() && getUser().phoneNumber,
    }),
    body: JSON.stringify({ requestId, offset }),
    signal: controller?.signal,
  });

  if (resp.status === 404) {
    const err = new Error('STREAM_EXPIRED');
    err.status = 404;
    throw err;
  }
  if (!resp.ok) {
    const err = new Error(`resume failed: ${resp.status}`);
    err.status = resp.status;
    throw err;
  }

  // 与主接口共用 parseChunkBuffer → offset 口径唯一
  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    const { parsed, rest } = parseChunkBuffer(buf);
    if (!parsed) continue;      // 半截分片，继续读
    buf = rest;
    for (const json of parsed) {
      onJson(json);
    }
  }
}

/**
 * 主动取消：用户确认终止后由 backgroundFlow.stopFlow 调用。
 * 后端语义：pop_entry + task.cancel —— 生成真正终止、缓存销毁。fire-and-forget。
 */
export function cancelStream(requestId) {
  if (!requestId) return;
  return fetch(`${getBaseURL()}/cancel-stream`, {
      method: 'POST',
      headers: new Headers({
          "Content-Type": CONTENT_TYPE_JSON,
          "Authorization": getToken(),
          "username": getUser() && getUser().phoneNumber,   // ★ 补齐，与 resumeStream 对齐
      }),
      body: JSON.stringify({ requestId }),
  }).catch(() => {});   // 保持 fire-and-forget 语义
}